/**
 * @type {number} Identifier for a standard draft type
 */
export const TYPE_ID_STANDARD = 0;

/**
 * @type {number} Identifier for a snake draft
 */
export const TYPE_ID_SNAKE = 1;

/**
 * @type {number} Identifier for a third round reversal draft
 */
export const TYPE_ID_3RR = 2;

/**
 * @type {number} Identifier for an auction draft
 */
export const TYPE_ID_AUCTION = 3;

/**
 * @type {array} Draft types with human readable labels
 * @todo - the order of these matters because the league settings modal uses
 * an array of these labels to display them, disregarding the actual values.
 * What we should do is update the league settings modal to not work assuming
 * the index in the array is reliable and use the actual values
 */
export const TYPE_OPTIONS = [
    { value: TYPE_ID_STANDARD, label: 'Standard', labelMobile: 'Linear' },
    { value: TYPE_ID_SNAKE, label: 'Snake', labelMobile: 'Snake' },
    { value: TYPE_ID_3RR, label: '3rd Round Reversal', labelMobile: '3RR' },
    { value: TYPE_ID_AUCTION, label: 'Auction', labelMobile: 'Auction' },
];

/**
 * @var int ID for 'rookies and vets' on `draft_player_pool_id`
 */
export const PLAYER_POOL_ID_ALL = 0;

/**
 * @var int ID for 'rookies only' on `draft_player_pool_id`
 */
export const PLAYER_POOL_ID_ROOKIES_ONLY = 1;

/**
 * @var int ID for 'rookies only' on `draft_player_pool_id`
 */
export const PLAYER_POOL_ID_VETS_ONLY = 2;

/**
 * @return array of player pool options for `draft_player_pool_id` field
 * in a `value` => `label` format.
 */
export const PLAYER_POOL_OPTIONS = [
    { value: PLAYER_POOL_ID_ALL, label: 'Vets & Rookies' },
    { value: PLAYER_POOL_ID_ROOKIES_ONLY, label: 'Rookie' },
    { value: PLAYER_POOL_ID_VETS_ONLY, label: 'Vets' },
];

/**
 * Returns the round that an overall pick number would be in for a draft of the
 * given league size.
 * @param {number} overallPickNumber
 * @param {number} leagueSize
 * @returns {number}
 */
export function getRound(overallPickNumber, leagueSize) {
    if (!leagueSize) return 0;
    return Math.ceil(overallPickNumber / leagueSize);
}

/**
 * Returns the pick number in a given round based on the overall pick number
 * and given league size.
 * @param {number} overallPickNumber
 * @param {number} leagueSize
 * @returns  {number}
 */
export function getPickInRound(overallPickNumber, leagueSize) {
    if (!leagueSize) return 0;
    return overallPickNumber % leagueSize === 0
        ? leagueSize // prettier-ignore
        : overallPickNumber % leagueSize;
}

/**
 * Gets a standard ADP display of {round}.{pick} based on the player's
 * overall pick number
 * @param {number} overallPickNumber The overall pick number for the player
 * @param {number} leagueSize The size of the league for the draft
 * @return string
 */
export function getPickDisplay(overallPickNumber, leagueSize) {
    // prettier-ignore
    return getRound(overallPickNumber, leagueSize) + '.' + 
        String(getPickInRound(overallPickNumber, leagueSize)).padStart(2, '0');
}

/**
 * Returns whether the draft order would be reversed for a round based on the
 * round and the draft type.
 * @param {number} draftTypeId
 * @param {number} round
 * @returns {boolean}
 */
export function getDraftOrderIsReversedForRound(draftTypeId, round) {
    // prettier-ignore
    return (
        (
            round % 2 == 0 && // --- we are in an even round
            (
                draftTypeId == TYPE_ID_SNAKE || // --- we are in a snake
                (
                    draftTypeId == TYPE_ID_3RR && // --- we are in a 3rr
                    round <= 2 // --- we are in the first two rounds
                )
            )
        ) ||
        (
            draftTypeId == TYPE_ID_3RR && // --- we are in a 3rr
            round >= 3 && // --- we are past the third round
            round % 2 == 1 // --- we are in an odd round
        )
    );
}

/**
 * @param {array} draftOrder Array of team ids in proper draft order for round 1
 * @param {number} draftTypeId For the type of draft to determine order for round
 * @param {number} overallPickNumber Overall pick number we want to know who would be picking for
 * @returns {number}
 */
export function getPickingTeamId(draftOrder, draftTypeId, overallPickNumber) {
    var draftOrderForRound = getDraftOrderForRound(
        draftOrder, // prettier-ignore
        draftTypeId,
        getRound(overallPickNumber, draftOrder.length)
    );
    var pickInRoundForOverallPick = getPickInRound(overallPickNumber, draftOrder.length);
    return draftOrderForRound[pickInRoundForOverallPick - 1];
}

/**
 * @param {array} draftOrder Array of ids of mvp_board_teams in drafting order
 * @param {bool} draftTypeId
 * @param {number} round
 * @returns {array}
 */
function getDraftOrderForRound(draftOrder, draftTypeId, round) {
    var newDraftOrder = JSON.parse(JSON.stringify(draftOrder));
    return getDraftOrderIsReversedForRound(draftTypeId, round) ? newDraftOrder.reverse() : newDraftOrder;
}
